import { useQuery } from '@tanstack/react-query';
import { listRows } from '@/api/resources';
import { farmerOptions, plotOptions, referenceValues, unitOptions } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';

type Option = { id: string; label: string };
type Picker = 'farmers' | 'plots' | 'workers' | 'stakeholders' | 'users' | 'harvests' | 'units' | 'species' | 'sites' | 'contractors' | 'parties' | 'chambers' | 'chemicals' | 'engagements';

/** The option lists the register forms share, fetched only for the pickers a form uses. */
export function usePickers(which: Picker[]) {
  const on = (name: Picker) => isSupabaseConfigured && which.includes(name);
  const farmers = useQuery({ queryKey: ['picker', 'farmers'], queryFn: () => farmerOptions(), enabled: on('farmers') });
  const plots = useQuery({ queryKey: ['picker', 'plots', ''], queryFn: () => plotOptions(), enabled: on('plots') });
  const workers = useQuery({
    queryKey: ['picker', 'workers'], enabled: on('workers'),
    queryFn: () => listRows<{ id: string; full_name: string; worker_code: string | null }>('workers', { orderBy: { column: 'full_name' } }),
  });
  const stakeholders = useQuery({
    queryKey: ['picker', 'stakeholders'], enabled: on('stakeholders'),
    queryFn: () => listRows<{ id: string; full_name: string; organisation_name: string | null; stakeholder_level: string }>('farm_stakeholders', { orderBy: { column: 'full_name' } }),
  });
  const users = useQuery({
    queryKey: ['picker', 'users'], enabled: on('users'),
    queryFn: () => listRows<{ id: string; full_name: string | null; email: string | null; status: string }>('users', { orderBy: { column: 'full_name' } }),
  });
  const harvests = useQuery({
    queryKey: ['picker', 'harvests'], enabled: on('harvests'),
    queryFn: () => listRows<{ id: string; harvest_code: string | null; plot_code: string | null; species_label: string | null; harvest_date: string; fsc_claim: string | null }>(
      'plot_harvest_entries', { orderBy: { column: 'harvest_date', ascending: false } }),
  });
  const sites = useQuery({
    queryKey: ['picker', 'sites'], enabled: on('sites'),
    queryFn: () => listRows<{ id: string; name: string; site_code: string | null; site_type: string; site_status: string; village_name: string | null }>('sites', { orderBy: { column: 'name' } }),
  });
  const contractors = useQuery({
    queryKey: ['picker', 'contractors'], enabled: on('contractors'),
    queryFn: () => listRows<{ id: string; name: string; contractor_code: string | null }>('contractors', { orderBy: { column: 'name' } }),
  });
  // Every party of the organisation, for an engagement: api.party_options (migration 310000).
  const parties = useQuery({
    queryKey: ['picker', 'parties'], enabled: on('parties'),
    queryFn: () => listRows<{ id: string; display_name: string; party_code: string | null; party_category: string; status: string; roles: string | null }>(
      'party_options', { orderBy: { column: 'display_name' } }),
  });
  // The processor's pieces (migration 320000): chambers by site, the active chemicals, and the
  // open engagements a load or run is done for.
  const chambers = useQuery({
    queryKey: ['picker', 'chambers'], enabled: on('chambers'),
    queryFn: () => listRows<{ id: string; name: string; site_name: string; chamber_type: string; status: string }>('chambers', { orderBy: { column: 'name' } }),
  });
  const chemicals = useQuery({
    queryKey: ['picker', 'chemicals'], enabled: on('chemicals'),
    queryFn: () => listRows<{ id: string; product_name: string; fsc_hhp: boolean; status: string }>('treatment_chemicals', { orderBy: { column: 'product_name' } }),
  });
  const engagements = useQuery({
    queryKey: ['picker', 'engagements'], enabled: on('engagements'),
    queryFn: () => listRows<{ id: string; engagement_code: string | null; party_name: string; role_type: string; status: string }>('party_engagements', { orderBy: { column: 'party_name' } }),
  });
  const units = useQuery({ queryKey: ['ref', 'unit', 'quantity'], queryFn: () => unitOptions('quantity'), enabled: on('units') });
  const species = useQuery({ queryKey: ['ref', 'species'], queryFn: () => referenceValues('species'), enabled: on('species') });

  return {
    farmers: (farmers.data ?? []) as Option[],
    plots: (plots.data ?? []) as Option[],
    workers: (workers.data ?? []).map((row) => ({ id: row.id, label: `${row.full_name}${row.worker_code ? ` (${row.worker_code})` : ''}` })),
    stakeholders: (stakeholders.data ?? []).map((row) => ({
      id: row.id, label: `${row.full_name}${row.organisation_name ? ` - ${row.organisation_name}` : ''}${row.stakeholder_level === 'organisation' ? ' (org)' : ''}`,
    })),
    users: (users.data ?? []).filter((row) => row.status === 'active').map((row) => ({ id: row.id, label: row.full_name ?? row.email ?? row.id })),
    harvests: (harvests.data ?? []).map((row) => ({
      id: row.id, label: `${row.harvest_code ?? ''} ${row.plot_code ?? ''} ${row.species_label ?? ''} (${row.harvest_date})`.replace(/\s+/g, ' ').trim(),
    })),
    harvestRows: harvests.data ?? [],
    sites: (sites.data ?? []).filter((row) => row.site_status !== 'closed').map((row) => ({
      id: row.id, label: `${row.name}${row.village_name ? ` - ${row.village_name}` : ''}`,
    })),
    // Where a chamber or tank can stand: a seasoning unit or treatment plant that is not closed.
    processingSites: (sites.data ?? []).filter((row) => row.site_status !== 'closed'
      && ['seasoning_unit', 'treatment_plant', 'sawmill', 'processing_unit'].includes(row.site_type)).map((row) => ({
      id: row.id, label: `${row.name}${row.village_name ? ` - ${row.village_name}` : ''}`,
    })),
    contractors: (contractors.data ?? []).map((row) => ({ id: row.id, label: `${row.name}${row.contractor_code ? ` (${row.contractor_code})` : ''}` })),
    chambers: (chambers.data ?? []).map((row) => ({ id: row.id, label: `${row.name} - ${row.site_name} (${row.chamber_type === 'kiln' ? 'kiln' : 'tank'})` })),
    // What kind each chamber is, so a load form can show the drying or the treatment fields.
    chamberTypes: Object.fromEntries((chambers.data ?? []).map((row) => [row.id, row.chamber_type])) as Record<string, string>,
    chemicals: (chemicals.data ?? []).filter((row) => row.status === 'active').map((row) => ({ id: row.id, label: `${row.product_name}${row.fsc_hhp ? ' (FSC HHP)' : ''}` })),
    engagements: (engagements.data ?? []).filter((row) => row.status === 'active').map((row) => ({
      id: row.id, label: `${row.party_name} as ${row.role_type.replace(/_/g, ' ')}${row.engagement_code ? ` (${row.engagement_code})` : ''}`,
    })),
    parties: (parties.data ?? []).filter((row) => row.status !== 'terminated').map((row) => ({
      id: row.id, label: `${row.display_name}${row.party_code ? ` (${row.party_code})` : ''}${row.roles ? ` - ${row.roles.replace(/_/g, ' ')}` : ''}`,
    })),
    units: (units.data ?? []).map((row) => ({ id: row.id, label: row.label })),
    species: (species.data ?? []).map((row) => ({ id: row.id, label: row.label })),
  };
}
